import { EventTimeService } from './../../../modules/event-calendar/services/event.time.service';
import { AbstractControl, FormControl } from '@angular/forms';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';

@Component({
  selector: 'ig-date-time-picker',
  templateUrl: './date.time.picker.component.html',
  styleUrls: ['./date.time.picker.component.scss'],
})
export class DateTimePickerComponent implements OnInit, OnChanges {
  @Input() startFormControl: FormControl; // for edit event, it is the real event start time
  @Input() endFormControl: FormControl;
  @Input() startTimeFormControl: FormControl;
  @Input() endTimeFormControl: FormControl;
  @Input() endDateInitOnly: string | null = null;
  @Input() mode: 'create-event' | 'itinerary' = 'create-event';
  @Input() timezoneFormatter: string;
  @Input() isAllDay: boolean; // Input to indicate if this is an all-day event (start time)
  @Input() isEndAllDay: boolean; // Input to indicate if the end time is all-day
  @Output() change: EventEmitter<any> = new EventEmitter();
  @Output() allDayChange: EventEmitter<boolean> = new EventEmitter();
  @Output() endAllDayChange: EventEmitter<boolean> = new EventEmitter();

  startControl: FormControl = new FormControl(); // this will be used to display the selected date
  endControl: FormControl = new FormControl();

  @ViewChild('start', { static: true }) startDateEle: MatFormField;
  @ViewChild('starttime', { static: true }) startTimeEle: MatFormField;
  @ViewChild('end', { static: true }) endDateEle: MatFormField;
  @ViewChild('endtime', { static: true }) endTimeEle: MatFormField;
  @ViewChild('startSelect', { static: true }) startSelect: MatSelect;
  @ViewChild('endSelect', { static: true }) endSelect: MatSelect;

  timeValues: {
    value: string;
    text: string;
  }[] = [];

  constructor(public timeService: EventTimeService, private cdr: ChangeDetectorRef) {
    dayjs.extend(utc);
    dayjs.extend(timezone);
  }

  // Get filtered end time values - only show times greater than start time when dates are the same
  get endTimeValues(): { value: string; text: string; }[] {
    // If no start time selected, show all times
    if (!this.startSelect?.value || !this.startControl?.value) {
      return this.timeValues;
    }

    // If end date is not set, show all times
    if (!this.endControl?.value) {
      return this.timeValues;
    }

    // If start and end dates are different, show all times
    const startDate = new Date(this.startControl.value);
    const endDate = new Date(this.endControl.value);
    
    // Check if dates are on the same day (ignoring time)
    const isSameDate = startDate.getFullYear() === endDate.getFullYear() &&
                       startDate.getMonth() === endDate.getMonth() &&
                       startDate.getDate() === endDate.getDate();

    if (!isSameDate) {
      return this.timeValues;
    }

    // If same date, filter to show only times greater than start time
    const startTimeValue = this.startSelect.value;
    
    // If start is "All Day", show all times for end (user can choose any end time)
    // Only the explicit ALL_DAY option should be treated as all-day
    if (!startTimeValue || startTimeValue === 'ALL_DAY') {
      return this.timeValues;
    }

    // Parse start time
    const startTimeParts = startTimeValue.split(':');
    if (startTimeParts.length !== 2) {
      return this.timeValues; // Invalid format, show all times
    }

    const [startHour, startMinute] = startTimeParts.map(Number);
    
    // Validate parsed values
    if (isNaN(startHour) || isNaN(startMinute)) {
      return this.timeValues;
    }

    const startTimeMinutes = startHour * 60 + startMinute;

    // Filter times to only show those greater than start time
    // But always include the currently selected end time to avoid overriding user's choice
    const currentEndTime = this.endSelect?.value;
    
    return this.timeValues.filter(time => {
      // Always show currently selected time (including ALL_DAY)
      if (time.value === currentEndTime || currentEndTime === 'ALL_DAY') {
        return true;
      }
      
      // Always show ALL_DAY option
      if (time.value === 'ALL_DAY') {
        return true;
      }
      
      // Parse and compare times
      const timeParts = time.value.split(':');
      if (timeParts.length !== 2) {
        return false;
      }
      
      const [hour, minute] = timeParts.map(Number);
      if (isNaN(hour) || isNaN(minute)) {
        return false;
      }
      
      const timeMinutes = hour * 60 + minute;
      return timeMinutes > startTimeMinutes;
    });
  }

  // Display function for mat-select to show "All Day" only when value is ALL_DAY
  getTimeDisplayText(value: string): string {
    if (!value) return '';
    // Check if it's "All Day" - only explicit ALL_DAY should be treated as all-day
    if (value === 'ALL_DAY') {
      return 'All Day';
    }
    // Find the matching time value and return its text
    const timeOption = this.timeValues.find(t => t.value === value);
    return timeOption ? timeOption.text : value;
  }

  get showStartDateError(): boolean {
    return !!this.startFormControl && this.startFormControl.touched &&
      this.startFormControl.invalid && !this.startControl?.value;
  }

  get showStartTimeError(): boolean {
    return !!this.startFormControl && this.startFormControl.touched &&
      this.startFormControl.invalid && !this.startSelect?.value;
  }

  get showEndDateError(): boolean {
    return !!this.endFormControl && this.endFormControl.touched &&
      this.endFormControl.invalid && !this.endControl?.value;
  }

  get showEndTimeError(): boolean {
    return !!this.endFormControl && this.endFormControl.touched &&
      this.endFormControl.invalid && !this.endSelect?.value;
  }

  ngOnInit(): void {
    this.setInitData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If form control values change after initialization (e.g., in edit mode), update the select values
    if (changes['startFormControl'] && this.startFormControl?.value && this.startSelect) {
      setTimeout(() => {
        this.updateStartTimeSelect();
      }, 150);
    }
    if (changes['endFormControl'] && this.endFormControl?.value && this.endSelect) {
      setTimeout(() => {
        this.updateEndTimeSelect();
      }, 150);
    }
    if (changes['isAllDay'] && this.startSelect) {
      setTimeout(() => {
        this.updateStartTimeSelect();
      }, 150);
    }
    if (changes['isEndAllDay'] && this.endSelect) {
      setTimeout(() => {
        this.updateEndTimeSelect();
      }, 150);
    }
  }

  private updateStartTimeSelect() {
    if (!this.startFormControl?.value || !this.startSelect) return;
    
    // Check if this is explicitly an all-day event
    if (this.isAllDay === true) {
      this.startSelect.value = 'ALL_DAY';
      this.cdr.detectChanges();
      return;
    }
    
    const startHour = this.timeService.getLocalHourByTZF(
      this.startFormControl.value,
      this.timezoneFormatter
    );
    const startMinute = dayjs(this.startFormControl.value).tz(this.timezoneFormatter).minute();

    // Always treat 00:00 as a valid time (e.g., 0:00 AM), not implicitly "All Day"
    let hour = this.timeService.getLocalTimeByTZF(
      this.startFormControl.value,
      this.timezoneFormatter
    );
    hour = hour.startsWith('0') && !hour.startsWith('0:') ? hour.slice(1) : hour;
    this.startSelect.value = `${hour}`;
    
    // Update the date control with the local date in the specified timezone
    // Create a Date object at local midnight (not UTC) for mat-datepicker
    const localDate = dayjs(this.startFormControl.value).tz(this.timezoneFormatter);
    const dateForPicker = new Date(localDate.year(), localDate.month(), localDate.date());
    this.startControl.setValue(dateForPicker);

    this.cdr.detectChanges();
  }

  private updateEndTimeSelect() {
    if (!this.endFormControl?.value || !this.endSelect) return;
    
    // Check if this is explicitly an all-day event (end time)
    if (this.isEndAllDay === true) {
      this.endSelect.value = 'ALL_DAY';
      this.cdr.detectChanges();
      return;
    }
    
    const endHour = this.timeService.getLocalHourByTZF(
      this.endFormControl.value,
      this.timezoneFormatter
    );
    const endMinute = dayjs(this.endFormControl.value).tz(this.timezoneFormatter).minute();

    // Always treat 00:00 as a valid time (e.g., 0:00 AM), not implicitly "All Day"
    let hour = this.timeService.getLocalTimeByTZF(
      this.endFormControl.value,
      this.timezoneFormatter
    );
    hour = hour.startsWith('0') && !hour.startsWith('0:') ? hour.slice(1) : hour;
    this.endSelect.value = `${hour}`;
    
    // Update the date control with the local date in the specified timezone
    // Create a Date object at local midnight (not UTC) for mat-datepicker
    const localDate = dayjs(this.endFormControl.value).tz(this.timezoneFormatter);
    const dateForPicker = new Date(localDate.year(), localDate.month(), localDate.date());
    this.endControl.setValue(dateForPicker);

    this.cdr.detectChanges();
  }

  setInitData() {
    if (!this.timezoneFormatter) {
      console.warn(
        'No timezone formatter set for date time picker! Default America/New_York will be used!'
      );
      this.timezoneFormatter = 'America/New_York';
    }

    this.setTimeSelector();

      setTimeout(() => {
      if (this.startFormControl.value) {
        // Check if this is explicitly an all-day event
        if (this.isAllDay === true) {
          if (!this.startSelect.value) {
            this.startSelect.value = 'ALL_DAY';
          }
        } else {
          const startHour = this.timeService.getLocalHourByTZF(
            this.startFormControl.value,
            this.timezoneFormatter
          );
          const startMinute = dayjs(this.startFormControl.value).tz(this.timezoneFormatter).minute();

          // Always treat 00:00 as a valid time (e.g., 0:00 AM), not implicitly "All Day"
          let hour = this.timeService.getLocalTimeByTZF(
            this.startFormControl.value,
            this.timezoneFormatter
          );
          hour = hour.startsWith('0') && !hour.startsWith('0:') ? hour.slice(1) : hour;
          if (!this.startSelect.value) {
            this.startSelect.value = `${hour}`;
          }
        }
        // Convert UTC ISO string to local date in the specified timezone
        // Create a Date object at local midnight (not UTC) for mat-datepicker
        const localDate = dayjs(this.startFormControl.value).tz(this.timezoneFormatter);
        const dateForPicker = new Date(localDate.year(), localDate.month(), localDate.date());
        this.startControl.setValue(dateForPicker);
      }
      if (this.endFormControl.value) {
        // Check if this is explicitly an all-day event (end time)
        if (this.isEndAllDay === true) {
          if (!this.endSelect.value) {
            this.endSelect.value = 'ALL_DAY';
          }
        } else {
          const endHour = this.timeService.getLocalHourByTZF(
            this.endFormControl.value,
            this.timezoneFormatter
          );
          const endMinute = dayjs(this.endFormControl.value).tz(this.timezoneFormatter).minute();

          // Always treat 00:00 as a valid time (e.g., 0:00 AM), not implicitly "All Day"
          let hour = this.timeService.getLocalTimeByTZF(
            this.endFormControl.value,
            this.timezoneFormatter
          );
          hour = hour.startsWith('0') && !hour.startsWith('0:') ? hour.slice(1) : hour;
          if (!this.endSelect.value) {
            this.endSelect.value = `${hour}`;
          }
        }

        // Convert UTC ISO string to local date in the specified timezone
        // Create a Date object at local midnight (not UTC) for mat-datepicker
        const localDate = dayjs(this.endFormControl.value).tz(this.timezoneFormatter);
        const dateForPicker = new Date(localDate.year(), localDate.month(), localDate.date());
        this.endControl.setValue(dateForPicker);
      } else if (this.endDateInitOnly) {
        // No end time set, but we still want to show the end date (e.g. custom-added date
        // with no end time). Leave endSelect null so End Time field stays empty.
        const localDate = dayjs(this.endDateInitOnly).tz(this.timezoneFormatter);
        const dateForPicker = new Date(localDate.year(), localDate.month(), localDate.date());
        this.endControl.setValue(dateForPicker);
      }
    }, 100);
  }

  private adjustWithLocalTimezone(timezoneDate: string, controlValue: string) {
    // if the date is not the same as the timezone date (user's local time), adjust the timezone date
    // for example, if user is in east Asia, this.startFormControl.value (ISO) is 2025-08-21, but timezoneDate might be 2025-08-22
    const date = this.timeService.toDateFormat(controlValue);
    let adjustedDate;
    if (date !== timezoneDate.split('T')[0]) {
      adjustedDate = this.timeService.toDate(controlValue, this.timezoneFormatter);
    }
    return adjustedDate || timezoneDate;
  }

  setFormControlValue(control: AbstractControl, timeSelect: MatSelect) {
    if (!control?.value) {
      return;
    }

    let date = new Date(control.value);

    // Format date with proper zero padding (YYYY-MM-DD HH:mm)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const timeValue = timeSelect.value || '00:00';
    const dateStr = `${year}-${month}-${day} ${timeValue}`;

    const iso = this.timeService.toISOTime(dateStr, this.timezoneFormatter);
    control.setValue(iso);

    this.change.emit();
  }

  // for editing event, when a date selected, set the event time to the selected time
  setFormControlValue1(control: AbstractControl, timeSelect: MatSelect, type: 'start' | 'end') {
    let date: Date;
    if (control?.value) {
      date = new Date(control.value);
    } else {``
      const existingIso = this[type + 'FormControl']?.value;
      if (!existingIso) {
        return;
      }
      const localDate = dayjs(existingIso).tz(this.timezoneFormatter);
      date = new Date(localDate.year(), localDate.month(), localDate.date());
    }

    // Convert ALL_DAY to 00:00 for storage
    const timeValue = timeSelect.value === 'ALL_DAY' ? '00:00' : (timeSelect.value || '00:00');
    
    // Emit whether this is an all-day event
    const isAllDaySelected = timeSelect.value === 'ALL_DAY';
    if (type === 'start') {
      this.allDayChange.emit(isAllDaySelected);
    } else if (type === 'end') {
      this.endAllDayChange.emit(isAllDaySelected);
    }

    // Format date with proper zero padding (YYYY-MM-DD HH:mm)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day} ${timeValue}`;

    const iso = this.timeService.toISOTime(dateStr, this.timezoneFormatter);
    
    if (this[type + 'FormControl']) {
      this[type + 'FormControl'].setValue(iso);
    }

    this.change.emit();
  }

  today() {
    return new Date();
  }

  onFocus() {
    this.setLabelWidth();
  }

  onStartDateChange() {
    if (!this.startControl?.value) {
      return;
    }
    if (!this.startSelect?.value) {
      return;
    }
    this.startFormControl?.markAsTouched();
    this.endFormControl?.markAsTouched();

    this.setFormControlValue1(this.startControl, this.startSelect, 'start');

    this.syncEndDateAfterStartChange();
  }

  private syncEndDateAfterStartChange() {
    if (!this.startControl?.value) {
      return;
    }

    const startDay = this.stripTime(this.startControl.value);
    const endDay = this.endControl?.value ? this.stripTime(this.endControl.value) : null;

    if (endDay && endDay.getTime() >= startDay.getTime()) {
      if (endDay.getTime() === startDay.getTime()) {
        this.fixEndTimeIfBeforeStart();
      }
      return;
    }

    this.endControl.setValue(new Date(startDay));

    if (!this.endSelect?.value) {
      this.endFormControl?.setValue(null);
      return;
    }

    this.fixEndTimeIfBeforeStart();
    if (this.endSelect.value) {
      this.setFormControlValue1(this.endControl, this.endSelect, 'end');
    } else {
      this.endFormControl?.setValue(null);
    }
  }

  private stripTime(value: Date | string): Date {
    const d = new Date(value);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // When start and end land on the same day, make sure the selected end time is still
  // after the start time; if not, bump it to the next available slot, or clear it.
  private fixEndTimeIfBeforeStart() {
    if (!this.endSelect || !this.startSelect?.value) {
      return;
    }

    const startVal = this.startSelect.value;
    const endVal = this.endSelect.value;
    if (!endVal || endVal === 'ALL_DAY' || startVal === 'ALL_DAY') {
      return;
    }

    const toMinutes = (v: string) => {
      const [h, m] = v.split(':').map(Number);
      return h * 60 + m;
    };

    if (toMinutes(endVal) > toMinutes(startVal)) {
      return;
    }

    const next = this.timeValues.find((t) => toMinutes(t.value) > toMinutes(startVal));
    this.endSelect.value = next ? next.value : null;
  }

  onEndDateChange() {
    if (!this.endControl?.value) {
      return;
    }
    if (!this.endSelect?.value) {
      return;
    }
    this.endFormControl?.markAsTouched();

    this.setFormControlValue1(this.endControl, this.endSelect, 'end');
  }

  clear() {
    if (this.startFormControl) {
      this.startFormControl.setValue(null);
    }
    if (this.endFormControl) {
      this.endFormControl.setValue(null);
    }
    if (this.startSelect) {
      this.startSelect.value = null;
    }
    if (this.endSelect) {
      this.endSelect.value = null;
    }
    if (this.startControl) {
      this.startControl.setValue(null);
    }
    if (this.endControl) {
      this.endControl.setValue(null);
    }
  }

  setTime(time: string | Date, timezoneFormatter: string, type: 'start' | 'end') {
    if (!time) return;
    if (type === 'start') {
      this.startFormControl.setValue(new Date(time).toISOString());
      const hour = this.timeService.getLocalHourByTZF(time, timezoneFormatter);
      const minute = dayjs(time).tz(timezoneFormatter).minute();

      // Always treat 00:00 as a valid time (e.g., 0:00 AM), not implicitly "All Day"
      this.startSelect.value = `${hour}:00`;
    } else {
      this.endFormControl.setValue(new Date(time).toISOString());
      const hour = this.timeService.getLocalHourByTZF(time, timezoneFormatter);
      const minute = dayjs(time).tz(timezoneFormatter).minute();

      // Always treat 00:00 as a valid time (e.g., 0:00 AM), not implicitly "All Day"
      this.endSelect.value = `${hour}:00`;
    }
  }

  private setTimeSelector() {
    if (this.mode === 'create-event') {
      for (let i = 0; i < 24; i++) {
        this.timeValues.push({
          value: `${i}:00`,
          text: `${i >= 13 ? i - 12 : i}:00 ` + (i >= 12 ? 'PM' : 'AM'),
        });
        this.timeValues.push({
          value: `${i}:15`,
          text: `${i >= 13 ? i - 12 : i}:15 ` + (i >= 12 ? 'PM' : 'AM'),
        });
        this.timeValues.push({
          value: `${i}:30`,
          text: `${i >= 13 ? i - 12 : i}:30 ` + (i >= 12 ? 'PM' : 'AM'),
        });
        this.timeValues.push({
          value: `${i}:45`,
          text: `${i >= 13 ? i - 12 : i}:45 ` + (i >= 12 ? 'PM' : 'AM'),
        });
      }
    }

    if (this.mode === 'itinerary') {
      for (let i = 0; i < 24; i++) {
        this.timeValues.push({
          value: `${i}:00`,
          text: `${i >= 12 ? i - 12 : i}:00 ` + (i >= 12 ? 'PM' : 'AM'),
        });
      }
    }
  }

  private setLabelWidth() {
    // solve the outline mode label not wide enough when focusing
    setTimeout(() => {
      const notch = this.startDateEle._elementRef.nativeElement.querySelector(
        '.mdc-notched-outline__notch'
      );
      notch && (notch.style.width = 70 + 'px');

      const notchEnd = this.endDateEle._elementRef.nativeElement.querySelector(
        '.mdc-notched-outline__notch'
      );
      notchEnd && (notchEnd.style.width = 70 + 'px');

      const notchStartTime = this.startTimeEle._elementRef.nativeElement.querySelector(
        '.mdc-notched-outline__notch'
      );
      notchStartTime && (notchStartTime.style.width = 40 + 'px');

      const notchEndTime = this.endTimeEle._elementRef.nativeElement.querySelector(
        '.mdc-notched-outline__notch'
      );
      notchEndTime && (notchEndTime.style.width = 40 + 'px');
    });
  }
}
